import { Descriptions, Flex, Layout, Spin, Table, Typography, Button } from "antd";
import React from "react";
import { useParams } from "react-router-dom";
import ErrorMessages from "../common/ErrorMessages";
import HeaderScreen from "../common/HeaderScreen";
import ReportButton from "../components/common/ReportButton";
import { SECONDARY_COLOR, WHITE_COLOR } from "../constants/color";
import { URL_REPORT } from "../constants/urls";
import { GET } from "../frameworks/HttpClient";
import { formatComma, dateTimeToHumanString, dateToHumanString, currencyFormat } from "../frameworks/Util";
import { PATH } from "../routes/CustomRoutes";
import { FilePdfOutlined } from "@ant-design/icons";
import _ from "lodash";

export default function QualityChemicalSummaryScreen() {
  const { id } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    if(id){
      fetchData();
    }
  },[id])

  const fetchData = async() => {
    setLoading(true);
    setErrorMessages(null);
    try {
      const response = await GET(`${URL_REPORT.QUALITY_CHEMICAL_SUMMARY}${id}/`);
      setData(response.data);
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <HeaderScreen title={`รายละเอียดสต๊อกสารเคมีคุณภาพ`} 
        style={{ backgroundColor : SECONDARY_COLOR, 
          color : WHITE_COLOR, 
          fontSize : 18, 
          fontWeight: 'bolder'
        }}
        onClick={() => window.close()}
      />
      <Layout.Content style={{ margin: 24, minHeight: 280, padding: "0px 24px", }}>
        <Spin spinning={loading}>
          <ErrorMessages message={errorMessages}/>
          <Descriptions
          size='small'
            title={
              <Flex justify="space-between" align="center">
                ข้อมูลเบื้องต้น
                <ReportButton url={`${URL_REPORT.QUALITY_CHEMICAL_DETAIL}${id}/`}>พิมพ์รายงาน</ReportButton>
              </Flex>
            }
            bordered
            column={1}
            items={[
              { label: 'แทงก์สารเคมี', children: data?.tank_name },
              { label: 'ล็อต', children: data?.lot_no },
              { label: 'วันที่เริ่มต้นการใช้งาน', children: dateTimeToHumanString(data?.created) },
              { label: 'น้ำหนัก', children: formatComma(data?.amount) },
              { label: 'สถานะล็อต', children: data?.is_closed ? 'ปิด' : 'เปิด' },
              { label: 'ผู้ทำรายการเริ่มต้น', children: data?.created_by_name },
          ]}/>
          <Typography.Title level={5}>ที่มาของสารเคมี</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[
              { title: 'ชื่อสารเคมี', dataIndex: 'chemical_name' },
              { title: 'วันที่ผสม', dataIndex: 'created', render: (value) => dateTimeToHumanString(value) },
              { title: 'ปริมาณ', dataIndex: 'amount', render: (value) => <div style={{textAlign: "right"}}>{formatComma(value)}</div> },
              { title: 'ล็อตการนำเข้า', dataIndex: 'lot_no'},
              { title: 'วันที่นำเข้า', dataIndex: 'input_date', render: (value) => dateToHumanString(value) },
              { title: 'คุณภาพ', dataIndex: 'is_qc_passed', render: (value) => value ? 'ผ่าน' : 'ไม่ผ่าน' },
              { title: 'อื่นๆ', dataIndex: 'note' },
            ]}
            dataSource={data?.mix_transactions}
            summary={pageData => {
              let total_amount = 0;
              pageData.forEach(({ amount }) => {
                total_amount += Number(amount);
              });
              return (
                <>{_.get(data, 'mix_transactions.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}>
                      <div style={{textAlign: "right"}}>{formatComma(total_amount)}</div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}/>
                    <Table.Summary.Cell index={4}/>
                    <Table.Summary.Cell index={5}/>
                    <Table.Summary.Cell index={6}/>
                  </Table.Summary.Row>}
                </>
              );
            }}/>
          <Typography.Title level={5}>ใช้ปรับปรุงยางสด</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[
              { title: 'ล็อตน้ำยางสด', dataIndex: 'lot_no', render: (value, original) => <span>{value} <Button type="link" icon={<FilePdfOutlined />} onClick={() => window.open(`${window.origin}${PATH.POND_SUMMARY}/${original.id}`)} /></span> },
              { title: 'วันที่ใช้งาน', dataIndex: 'created', render: (value) => dateTimeToHumanString(value) },
              { title: 'ปริมาณสั่ง', dataIndex: 'amount', render: (value) => formatComma(value) },
              { title: 'ผู้สั่งงาน', dataIndex: 'lab_user' },
              { title: 'ผู้รับงาน', dataIndex: 'input_user' },
            ]}
            dataSource={data?.pond_transactions}
            summary={pageData => {
              let total_amount = 0;
              pageData.forEach(({ amount }) => {
                total_amount += Number(amount);
              });
              return (
                <>{_.get(data, 'pond_transactions.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}>{total_amount}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3}/>
                    <Table.Summary.Cell index={4}/>
                  </Table.Summary.Row>}
                </>
              );
            }}/>
          <Typography.Title level={5}>ใช้ปรับปรุงยางข้น</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[
              { title: 'ล็อตน้ำยางข้น', dataIndex: 'lot_no', render: (value, original) => <span>{value} <Button type="link" icon={<FilePdfOutlined />} onClick={() => window.open(`${window.origin}${PATH.TANK_SUMMARY}/${original.id}`)} /></span> },
              { title: 'วันที่ใช้งาน', dataIndex: 'created', render: (value) => dateTimeToHumanString(value) },
              { title: 'ปริมาณสั่ง', dataIndex: 'amount', render: (value) => formatComma(value) },
              { title: 'ผู้สั่งงาน', dataIndex: 'lab_user' },
              { title: 'ผู้รับงาน', dataIndex: 'input_user' },
            ]}
            dataSource={data?.tank_transactions}
            summary={pageData => {
              let total_amount = 0;
              pageData.forEach(({ amount }) => {
                total_amount += Number(amount);
              });
              return (
                <>{_.get(data, 'tank_transactions.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}>{total_amount}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3}/>
                    <Table.Summary.Cell index={4}/>
                  </Table.Summary.Row>}
                </>
              );
            }}/>
          <Typography.Title level={5}>จ่ายผู้ขายน้ำยางสด</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[
              { title: 'ผู้ขาย', dataIndex: 'supplier_name' },
              { title: 'วันที่ใช้งาน', dataIndex: 'date', render: (value) => dateToHumanString(value) },
              { title: 'ปริมาณ NH4OH', dataIndex: 'nh4oh', render: value => (<div style={{textAlign: "right"}}>{currencyFormat(value)}</div>)},
              { title: 'ปริมาณ TZ', dataIndex: 'tz', render: value => (<div style={{textAlign: "right"}}>{currencyFormat(value)}</div>)},
              { title: 'ผู้สั่งงาน', dataIndex: 'created_by_name' },
            ]}
            dataSource={data?.supplier_chemical_transfer}
            summary={pageData => {
              let total_nh4oh = 0;
              let total_tz = 0;
              pageData.forEach(({ nh4oh, tz }) => {
                total_nh4oh += Number(nh4oh);
                total_tz += Number(tz)
              });
              return (
                <>{_.get(data, 'supplier_chemical_transfer.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}>
                    <div style={{textAlign: "right"}}>{currencyFormat(total_nh4oh)}</div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <div style={{textAlign: "right"}}>{currencyFormat(total_tz)}</div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}/>
                  </Table.Summary.Row>}
                </>
              );
            }}
            />
        </Spin>
      </Layout.Content>
    </Layout>
  )
}